import * as z from 'zod';

// Enums
export const crmProviderTypeEnum = z.enum([
  'hubspot',
  'salesforce',
  'attio',
  'rudderstack',
  'google_ads',
  'linkedin_ads',
]);
export type CrmProviderType = z.infer<typeof crmProviderTypeEnum>;

/** Providers that sync ad campaigns (the ADWORD campaign axis). */
export const adsProviderTypeEnum = z.enum(['google_ads', 'linkedin_ads']);
export type AdsProviderType = z.infer<typeof adsProviderTypeEnum>;

export const crmIntegrationStatusEnum = z.enum(['pending', 'ready', 'error']);
export type CrmIntegrationStatus = z.infer<typeof crmIntegrationStatusEnum>;

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readCrmIntegrationRefSchema = z.object({
  id: z.uuid(),
  providerType: crmProviderTypeEnum,
});
export type ReadCrmIntegrationRef = z.infer<typeof readCrmIntegrationRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const crmIntegrationRefSchema = z.object({
  id: z.uuid(),
});
export type CrmIntegrationRef = z.infer<typeof crmIntegrationRefSchema>;

// Per-provider integration settings, discriminated by providerType. The
// discriminator values pass verbatim on the wire (enum values, not converted
// keys). Google Ads currently has no configurable fields (variant generation
// is opt-in per Tinct campaign, not an integration setting); null for
// providers without settings.
export const googleAdsIntegrationSettingsSchema = z.object({
  providerType: z.literal('google_ads'),
});
export type GoogleAdsIntegrationSettings = z.infer<typeof googleAdsIntegrationSettingsSchema>;

// LinkedIn Ads: which ad accounts the sync covers. An empty list (or null)
// means every ad account the connected member can read.
export const linkedinAdsIntegrationSettingsSchema = z.object({
  providerType: z.literal('linkedin_ads'),
  adAccountIds: z.array(z.string()).nullable(),
});
export type LinkedinAdsIntegrationSettings = z.infer<typeof linkedinAdsIntegrationSettingsSchema>;

export const crmIntegrationSettingsSchema = z.discriminatedUnion('providerType', [
  googleAdsIntegrationSettingsSchema,
  linkedinAdsIntegrationSettingsSchema,
  // future providers: one member each
]);
export type CrmIntegrationSettings = z.infer<typeof crmIntegrationSettingsSchema>;

// 3. Read schema - all properties nullable (API response)
export const readCrmIntegrationSchema = z.object({
  id: z.uuid().nullable(),
  ownerId: z.uuid().nullable(),
  ownerType: z.string().nullable(),
  providerType: crmProviderTypeEnum.nullable(),
  status: crmIntegrationStatusEnum.nullable(),
  errorDetails: z.string().nullable(),
  lastSyncAt: z.iso.datetime().nullable(),
  connectedAt: z.iso.datetime().nullable(),
  settings: crmIntegrationSettingsSchema.nullable(),
  /**
   * When the stored access token expires. Meaningful for providers without a
   * refresh token (LinkedIn: 60 days) — show a "reconnect before …" banner
   * when it is within 7 days.
   */
  tokenExpiresAt: z.iso.datetime().nullable(),
  /**
   * True when the connection is dead (status `error` with a
   * `REAUTHORIZATION_REQUIRED:` error) or the stored token is already past
   * its expiry — the user must run the OAuth flow again.
   */
  needsReauthorization: z.boolean().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadCrmIntegration = z.infer<typeof readCrmIntegrationSchema>;

// Update payload for the per-provider settings (PUT /settings). Only
// LinkedIn Ads has configurable fields today: `adAccountIds` omitted keeps the
// stored selection, `[]` syncs every account.
export const updateCrmIntegrationSettingsSchema = z.object({
  providerType: z.literal('linkedin_ads'),
  adAccountIds: z.array(z.string()).optional(),
});
export type UpdateCrmIntegrationSettings = z.infer<typeof updateCrmIntegrationSettingsSchema>;

// OAuth URL Response
export const oAuthUrlResponseSchema = z.object({
  authorizationUrl: z.string(),
  providerType: crmProviderTypeEnum,
});
export type OAuthUrlResponse = z.infer<typeof oAuthUrlResponseSchema>;

// Create payload - connect a provider with direct credentials.
// The `credentials` map keys are the credential field names exposed by /providers.
export const createCrmIntegrationSchema = z.object({
  organizationId: z.uuid(),
  providerType: crmProviderTypeEnum,
  credentials: z.record(z.string(), z.string()),
});
export type CreateCrmIntegration = z.infer<typeof createCrmIntegrationSchema>;

// RudderStack credential fields (used by the configuration form)
export const rudderstackCredentialsSchema = z.object({
  writeKey: z.string().min(1, 'Write key is required.'),
  dataPlaneUrl: z
    .string()
    .min(1, 'Data plane URL is required.')
    .url('Data plane URL must be a valid URL.'),
});
export type RudderstackCredentials = z.infer<typeof rudderstackCredentialsSchema>;

// CRM Auth Info - non-secret configuration of a direct-credentials integration.

// 3. Read schema - all properties nullable (API response).
// The `writeKey` is masked: all but its last 3 characters are replaced with `*`.
export const readCrmAuthInfoSchema = z.object({
  dataPlaneUrl: z.string().nullable(),
  writeKey: z.string().nullable(),
});
export type ReadCrmAuthInfo = z.infer<typeof readCrmAuthInfoSchema>;

// Update payload - the `credentials` map keys are the credential field names
// exposed by /providers (same shape as create). Secret fields (e.g. the write
// key) may be omitted to keep the currently stored value, so the configuration
// can be edited without re-sending the secret.
export const updateCrmAuthInfoSchema = z.object({
  credentials: z.record(z.string(), z.string()),
});
export type UpdateCrmAuthInfo = z.infer<typeof updateCrmAuthInfoSchema>;

// Legacy exports for backward compatibility
export const crmAuthInfoSchema = readCrmAuthInfoSchema;
export type CrmAuthInfo = ReadCrmAuthInfo;

// Legacy exports for backward compatibility
export const crmIntegrationSchema = readCrmIntegrationSchema;
export type CrmIntegration = ReadCrmIntegration;

// CRM Segment (company list from CRM)

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readCrmSegmentRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type ReadCrmSegmentRef = z.infer<typeof readCrmSegmentRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const crmSegmentRefSchema = z.object({
  id: z.uuid(),
});
export type CrmSegmentRef = z.infer<typeof crmSegmentRefSchema>;

// 3. Read schema - all properties nullable (API response)
export const readCrmSegmentSchema = z.object({
  id: z.uuid().nullable(),
  crmId: z.string().nullable(),
  name: z.string().nullable(),
  memberCount: z.number().nullable(),
});
export type ReadCrmSegment = z.infer<typeof readCrmSegmentSchema>;

// Legacy exports for backward compatibility
export const crmSegmentSchema = readCrmSegmentSchema;
export type CrmSegment = ReadCrmSegment;

// Keyword entry of a synced ads campaign: text + match type (exact/phrase/broad).
export const adsCampaignKeywordSchema = z.object({
  text: z.string().nullable(),
  matchType: z.string().nullable(),
});
export type AdsCampaignKeyword = z.infer<typeof adsCampaignKeywordSchema>;

// Ad campaign (Google Ads or LinkedIn Ads), synced from the connected account
// (read-only rows — written exclusively by the sync). Matches are flat ids,
// not nested refs.
export const readAdsCampaignSchema = z.object({
  id: z.uuid().nullable(),
  integrationId: z.uuid().nullable(),
  /** Provider-side account id (Google Ads customer id / LinkedIn ad account id, digits). */
  externalCustomerId: z.string().nullable(),
  /**
   * The provider-side campaign id (numeric string) — the edge selection key
   * (`?tinct_ad=`). Google Ads injects it through the `{campaignid}` URL
   * macro; LinkedIn through the dynamic tracking parameter
   * `tinct_ad = CAMPAIGN_ID` set once in Campaign Manager.
   */
  externalCampaignId: z.string().nullable(),
  name: z.string().nullable(),
  status: z.string().nullable(),
  /**
   * Google Ads: SEARCH, DISPLAY, … LinkedIn Ads: SPONSORED_UPDATES, TEXT_AD,
   * SPONSORED_INMAILS, DYNAMIC.
   */
  advertisingChannelType: z.string().nullable(),
  finalUrls: z.array(z.string()).nullable(),
  /** Always empty for LinkedIn Ads (no keyword targeting). */
  keywords: z.array(adsCampaignKeywordSchema).nullable(),
  adHeadlines: z.array(z.string()).nullable(),
  adDescriptions: z.array(z.string()).nullable(),
  /** Tinct campaign the final URLs resolved to; null = synced-but-unmatched. */
  matchedCampaignId: z.uuid().nullable(),
  matchedLandingPageId: z.uuid().nullable(),
  /** The URL that produced the match. */
  matchNormalizedUrl: z.string().nullable(),
  lastSyncedAt: z.iso.datetime().nullable(),
});
export type ReadAdsCampaign = z.infer<typeof readAdsCampaignSchema>;

export const adsCampaignRefSchema = z.object({
  id: z.uuid(),
});
export type AdsCampaignRef = z.infer<typeof adsCampaignRefSchema>;

// Ad account readable by the connected member (LinkedIn Ads account picker).
// Live from the provider; `selected` reflects the integration settings — an
// empty selection means every account, and every row reads `selected: true`.

// 3. Read schema - all properties nullable (API response)
export const readAdsAccountSchema = z.object({
  /** Provider-side account id (digits). */
  customerId: z.string().nullable(),
  name: z.string().nullable(),
  manager: z.boolean().nullable(),
  /** ISO-4217 currency of the account. */
  currency: z.string().nullable(),
  selected: z.boolean().nullable(),
});
export type ReadAdsAccount = z.infer<typeof readAdsAccountSchema>;

// Daily performance metrics of a synced ad campaign (one row per campaign
// per UTC day). Written by the sync; aggregate client-side (sums, CTR, CPC…).

// 3. Read schema - all properties nullable (API response)
export const readAdsCampaignMetricSchema = z.object({
  id: z.uuid().nullable(),
  adsCampaignId: z.uuid().nullable(),
  externalCampaignId: z.string().nullable(),
  metricDate: z.iso.date().nullable(),
  impressions: z.number().nullable(),
  clicks: z.number().nullable(),
  landingPageClicks: z.number().nullable(),
  /** Spend in the ad account's currency. */
  costLocal: z.number().nullable(),
  /** ISO-4217 currency of `costLocal`. */
  currency: z.string().nullable(),
  costUsd: z.number().nullable(),
  /** Website conversions (post-click + post-view). */
  conversions: z.number().nullable(),
  postClickConversions: z.number().nullable(),
  /** Lead-gen form submissions. */
  leads: z.number().nullable(),
  engagements: z.number().nullable(),
  likes: z.number().nullable(),
  comments: z.number().nullable(),
  shares: z.number().nullable(),
  follows: z.number().nullable(),
  videoViews: z.number().nullable(),
  /** Unique members reached (only for windows ≤ 92 days). */
  approximateMemberReach: z.number().nullable(),
  /** Reach ÷ target audience size, 0–1 (only for windows ≤ 92 days). */
  audiencePenetration: z.number().nullable(),
  syncedAt: z.iso.datetime().nullable(),
});
export type ReadAdsCampaignMetric = z.infer<typeof readAdsCampaignMetricSchema>;
